import { FC, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from '../ui/use-toast';
import { RotateCcw } from 'lucide-react';
import { useLessonProgress } from '@/hooks/useLessonProgress';
import { cn } from '@/lib/utils';
import { Confetti } from '../ui/confetti';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface QuizQuestion {
  id: string;
  question: string;
  correct_answer: string;
  wrong_answers?: string[];
  options?: string[];
  lesson_id: string;
  active: boolean;
}

interface LessonQuizProps {
  lessonId: string; // Este es un UUID
  questions: QuizQuestion[];
  onComplete: () => void;
}

export const LessonQuiz: FC<LessonQuizProps> = ({ lessonId, questions, onComplete }) => {
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const { updateProgress, calculateCerebrixReward } = useLessonProgress(lessonId);
  const { user } = useAuth();

  const queryClient = useQueryClient();

  const currentQuestion = questions[currentQuestionIndex];

  // Si no hay preguntas, mostrar mensaje
  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-lg text-gray-600">No hay preguntas disponibles para este quiz.</p>
        <Button onClick={() => navigate('/lessons')} className="mt-4">
          Volver a lecciones
        </Button>
      </div>
    );
  }

  // Preparar las opciones de respuesta
  const getAnswerOptions = (question: QuizQuestion) => {
    if (question.options && question.options.length > 0) {
      return question.options;
    }
    
    // Si no hay opciones predefinidas, crear a partir de wrong_answers y correct_answer
    const options = [
      ...(question.wrong_answers || []),
      question.correct_answer
    ].filter(Boolean);

    // Mezclar las opciones
    return options.sort(() => Math.random() - 0.5);
  };

  const handleAnswer = async (selectedAnswer: string) => {
    const isCorrect = currentQuestion?.correct_answer === selectedAnswer;
    const newCorrectAnswers = correctAnswers + (isCorrect ? 1 : 0);
    
    if (isCorrect) {
      setCorrectAnswers(newCorrectAnswers);
      
      // Mostrar confetti para respuestas correctas
      const confetti = new Confetti();
      confetti.addConfetti({
        emojis: ['✨', '⭐', '🌟'],
        emojiSize: 50,
        confettiNumber: 30,
      });
      
      toast({
        title: "¡Respuesta Correcta! 🎉",
        description: (
          <div className="space-y-2">
            <p className="text-green-600 font-bold text-lg animate-bounce">+4 puntos</p>
            <div className="text-sm text-gray-600">
              ¡Sigue así! Has ganado {newCorrectAnswers * 4} puntos en este quiz
            </div>
          </div>
        ),
        className: "bg-green-50 border-2 border-green-500 shadow-lg",
      });
    } else {
      toast({
        title: "¡Casi lo tienes! 💪",
        description: (
          <div className="space-y-2">
            <p className="text-red-600">La respuesta correcta era:</p>
            <p className="font-semibold text-gray-800 bg-red-50 p-2 rounded-md border border-red-200">
              {currentQuestion?.correct_answer}
            </p>
            <p className="text-sm text-gray-600">¡No te desanimes, sigue intentándolo!</p>
          </div>
        ),
        variant: "destructive",
        className: "bg-red-50 border-2 border-red-500 shadow-lg",
      });
    }

    // Esperar un momento antes de cambiar la pregunta
    setTimeout(async () => {
      if (currentQuestionIndex + 1 < questions.length) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else {
        setQuizCompleted(true);
        
        try {
          // Actualizar progreso usando el currentScore que incluye la última respuesta
          const result = await updateProgress.mutateAsync({
            type: 'quiz',
            score: newCorrectAnswers,  // Enviamos el número de respuestas correctas
            completed_at: new Date().toISOString()
          });
          
          // Solo mostrar el toast de completado si ganó puntos y no se ha completado antes
          if (result.points_earned > 0) {
            // Obtener el progreso actual de la lección
            const { data: progress } = await supabase
              .from('lesson_progress')
              .select('*')
              .eq('user_id', user?.id)
              .eq('lesson_id', lessonId)
              .single();

            // Solo mostrar el toast de resumen si es la primera vez que se completa
            if (!progress?.completed_at || new Date(progress.completed_at) > new Date(Date.now() - 5000)) {
              toast({
                title: "¡Lección Completada! 🎉",
                description: (
                  <div className="space-y-2">
                    <p className="font-semibold">Actividades completadas:</p>
                    {progress?.video_watched && (
                      <div className="flex justify-between">
                        <span>Video:</span>
                        <span className="text-primary font-bold">30 puntos</span>
                      </div>
                    )}
                    {progress?.audio_listened && (
                      <div className="flex justify-between">
                        <span>Audio:</span>
                        <span className="text-primary font-bold">20 puntos</span>
                      </div>
                    )}
                    {progress?.content_read && (
                      <div className="flex justify-between">
                        <span>Lectura:</span>
                        <span className="text-primary font-bold">10 puntos</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Quiz:</span>
                      <span className="text-primary font-bold">{newCorrectAnswers * 4} puntos</span>
                    </div>
                    <div className="mt-2 pt-2 border-t flex justify-between font-bold">
                      <span>Total:</span>
                      <span className="text-primary">
                        {(progress?.video_watched ? 30 : 0) + 
                         (progress?.audio_listened ? 20 : 0) + 
                         (progress?.content_read ? 10 : 0) + 
                         (newCorrectAnswers * 4)} puntos
                      </span>
                    </div>
                  </div>
                ),
                className: "bg-green-50 border-2 border-green-500 shadow-lg",
              });
            }
          }
          
          // Invalidar queries para actualizar datos en tiempo real
          queryClient.invalidateQueries({ queryKey: ['lessonProgress'] });
          queryClient.invalidateQueries({ queryKey: ['achievements'] });
          queryClient.invalidateQueries({ queryKey: ['userProgress'] });
          queryClient.invalidateQueries({ queryKey: ['userCerebrix'] });
          queryClient.invalidateQueries({ queryKey: ['dashboard'] });
          
          onComplete();
        } catch (error) {
          console.error('Error saving quiz progress:', error);
          toast({
            title: "Error",
            description: "No se pudo guardar el progreso. Por favor, inténtalo de nuevo.",
            variant: "destructive",
          });
        }
      }
    }, 1000);
  };

  if (quizCompleted) {
    return (
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold">¡Quiz completado!</h2>
        <p className="text-xl">Tu puntuación: {correctAnswers * 4} de {questions.length * 4}</p>
        <div className="flex gap-4 justify-center">
          <Button 
            variant="outline" 
            onClick={() => {
              setCurrentQuestionIndex(0);
              setCorrectAnswers(0);
              setQuizCompleted(false);
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Intentar nuevamente
          </Button>
          <Button onClick={() => navigate('/lessons')}>
            Volver a lecciones
          </Button>
        </div>
      </div>
    );
  }

  // Si no hay pregunta actual, mostrar mensaje
  if (!currentQuestion) {
    return (
      <div className="text-center py-8">
        <p className="text-lg text-gray-600">No hay más preguntas disponibles.</p>
        <Button onClick={() => navigate('/lessons')} className="mt-4">
          Volver a lecciones
        </Button>
      </div>
    );
  }

  // Obtener las opciones para la pregunta actual
  const answerOptions = getAnswerOptions(currentQuestion);

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Pregunta {currentQuestionIndex + 1} de {questions.length}</h2>
        <p className="text-sm text-muted-foreground">Puntos: {correctAnswers * 4}</p>
      </div>
      
      <Progress value={(currentQuestionIndex / questions.length) * 100} />
      
      <div className="space-y-4">
        <p className="text-lg break-words">{currentQuestion.question}</p>
        <div className="grid gap-4">
          {answerOptions.map((option, index) => (
            <Button
              key={index}
              variant="outline"
              className={cn(
                "justify-start px-4 py-8 h-auto w-full text-left break-words whitespace-normal",
                "bg-white text-foreground",
                "hover:bg-gray-50 hover:text-foreground",
                "focus:bg-white focus:text-foreground focus:ring-0",
                "active:bg-white active:text-foreground active:ring-0",
                "focus-visible:ring-0 focus-visible:ring-offset-0"
              )}
              style={{
                WebkitTapHighlightColor: 'transparent',
                outline: 'none'
              }}
              onClick={() => handleAnswer(option)}
            >
              {option}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};